import * as Localization from "expo-localization";
import translationEN from "./locale/en/translation.json";
import translationIT from "./locale/it/translation.json";
import translationFR from "./locale/fr/translation.json";
import translationDE from "./locale/de/translation.json";
import translationES from "./locale/es/translation.json";
import translationNL from "./locale/nl/translation.json";
import translationFI from "./locale/fi/translation.json";
import translationPT from "./locale/pt/translation.json";
import translationPL from "./locale/pl/translation.json";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import AsyncStorage from "@react-native-async-storage/async-storage";
import dayjs from "dayjs";
import "dayjs/locale/it";
import "dayjs/locale/fr";
import "dayjs/locale/de";
import "dayjs/locale/es";
import "dayjs/locale/nl";
import "dayjs/locale/fi";
import "dayjs/locale/pt";
import "dayjs/locale/pl";
import * as yup from "yup";

const LANGUAGE_KEY = "@toothfairy_language";

export const AVAILABLE_LANGUAGES = [
  { code: "en", name: "English", nativeName: "English" },
  { code: "it", name: "Italian", nativeName: "Italiano" },
  { code: "fr", name: "French", nativeName: "Français" },
  { code: "de", name: "German", nativeName: "Deutsch" },
  { code: "es", name: "Spanish", nativeName: "Español" },
  { code: "nl", name: "Dutch", nativeName: "Nederlands" },
  { code: "fi", name: "Finnish", nativeName: "Suomi" },
  { code: "pt", name: "Portuguese", nativeName: "Português" },
  { code: "pl", name: "Polish", nativeName: "Polski" },
];

const yupLocale = {
  en: {
    string: {
      required: ({ path }) => `${path} is a required field`,
      email: ({ path }) => `${path} must be a valid email`,
      min: ({ path, min }) => `${path} must be at least ${min} characters`,
      max: ({ path, max }) => `${path} must be at most ${max} characters`,
    },
    number: {
      required: ({ path }) => `${path} is a required field`,
      min: ({ path, min }) => `${path} must be at least ${min}`,
      max: ({ path, max }) => `${path} must be at most ${max}`,
    },
    mixed: {
      required: ({ path }) => `${path} is a required field`,
      oneOf: ({ path }) => `${path} must be one of the allowed values`,
    },
  },
  it: {
    string: {
      required: ({ path }) => `${path} è un campo obbligatorio`,
      email: ({ path }) => `${path} deve essere un'email valida`,
      min: ({ path, min }) => `${path} deve essere di almeno ${min} caratteri`,
      max: ({ path, max }) =>
        `${path} deve essere di al massimo ${max} caratteri`,
    },
    number: {
      required: ({ path }) => `${path} è un campo obbligatorio`,
      min: ({ path, min }) => `${path} deve essere almeno ${min}`,
      max: ({ path, max }) => `${path} deve essere al massimo ${max}`,
    },
    mixed: {
      required: ({ path }) => `${path} è un campo obbligatorio`,
      oneOf: ({ path }) => `${path} deve essere uno dei valori consentiti`,
    },
  },
};

export const setYupLocale = (lang) => {
  const locale = yupLocale[lang] || yupLocale.en;
  yup.setLocale(locale);
};

const getStoredLanguage = async () => {
  try {
    const storedLang = await AsyncStorage.getItem(LANGUAGE_KEY);
    return storedLang;
  } catch (error) {
    return null;
  }
};

export const setStoredLanguage = async (languageCode) => {
  try {
    await AsyncStorage.setItem(LANGUAGE_KEY, languageCode);
    return true;
  } catch (error) {
    return false;
  }
};

export const changeLanguage = async (languageCode) => {
  if (!languageCode) return;
  await setStoredLanguage(languageCode);
  await i18n.changeLanguage(languageCode);
  dayjs.locale(languageCode);
  setYupLocale(languageCode);
};

const resources = {
  en: {
    translation: translationEN,
  },
  it: {
    translation: translationIT,
  },
  fr: {
    translation: translationFR,
  },
  de: {
    translation: translationDE,
  },
  es: {
    translation: translationES,
  },
  nl: {
    translation: translationNL,
  },
  fi: {
    translation: translationFI,
  },
  pt: {
    translation: translationPT,
  },
  pl: {
    translation: translationPL,
  },
};

const getInitialLanguage = async () => {
  const storedLang = await getStoredLanguage();
  if (storedLang && AVAILABLE_LANGUAGES.some((l) => l.code === storedLang)) {
    return storedLang;
  }
  const deviceLocale = Localization.locale?.split?.("-")?.[0] || "en";
  if (AVAILABLE_LANGUAGES.some((l) => l.code === deviceLocale)) {
    return deviceLocale;
  }
  return "en";
};

let isInitialized = false;

export const initI18n = async () => {
  if (isInitialized) return i18n;

  const initialLanguage = await getInitialLanguage();

  dayjs.locale(initialLanguage);
  setYupLocale(initialLanguage);

  await i18n.use(initReactI18next).init({
    resources,
    fallbackLng: "en",
    debug: false,
    lng: initialLanguage,
    interpolation: {
      escapeValue: false,
    },
    nsSeparator: false,
    keySeparator: ".",
  });

  isInitialized = true;
  return i18n;
};

if (!isInitialized) {
  initI18n();
}

export default i18n;
